// install (please try to align the version of installed @nivo packages)
// yarn add @nivo/pie
import { Pie, ResponsivePie } from '@nivo/pie';
import useFetch from '../../api/useFetch';
import {
  deliveryAssignmentDailyPreviewGetApi,
  DeliveryPreviewType,
} from '../../api/apiConfig';
import { useEffect, useState } from 'react';
import { getYearMonthDateFromDate } from '../../function/replaceString';
import styled, { useTheme } from 'styled-components';
import { IoIosArrowBack } from 'react-icons/io';
import { IoIosArrowForward } from 'react-icons/io';

//차트 개별데이터 타입
type DataType = {
  id: string;
  label: string;
  value: number;
  color: string;
};

//차트 배송원별 묶음데이터 타입
type DataGroupType = {
  deliveryPersonName: string;
  dataList: DataType[];
};

//차트 색상 묶음데이터 타입
type ColorParreteGroupType = {
  deliveryPersonName: string;
  colorList: string[];
};

//DeliveryAssignmentPreview를 배송원별로 묶는 데이터 타입
type DeliveryPreviewGroupType = {
  deliveryPersonName: string;
  deliveryAssignmentList: DeliveryPreviewType[];
};

//배송완료항목 채울 차트컬러
const fillColor = 'hsl(135 58% 60% / 1)';

//배송중항목 채울 차트컬러
const processColor = 'hsl(135 58% 85% / 1)';

//배송대기항목 채울 차트컬러
const emptyColor = 'hsl(135 58% 95% / 1)';

export const DailyDeliveryChart = () => {
  const theme = useTheme();

  //차트 컬러리스트를 배송원별로 묶어 담아둘 state
  const [colorParette, setColorParette] = useState<ColorParreteGroupType[]>([]);

  //차트 데이터를 배송원별로 묶어 담아둘 state
  const [chartDataList, setChartDataList] = useState<DataGroupType[]>([]);

  //현재 선택된 차트데이터
  const [selectedChartData, setSelectedChartData] =
    useState<DataGroupType | null>(null);

  //현재 선택된 컬러리스트 데이터
  const [selectedColorData, setSelectedColorData] =
    useState<ColorParreteGroupType | null>(null);

  //오늘의 배송일정 조회
  const { fetch: fetchDeliveryAssignmentDailyPreviewGetApi } = useFetch({
    fetchFunction: deliveryAssignmentDailyPreviewGetApi,
    onSuccess: (data: DeliveryPreviewType[]) => groupingData(data),
  });

  //컬러, 차트데이터 초기화 후 첫번째 인덱스를 기본값으로
  useEffect(() => {
    if (colorParette.length < 1) return;
    if (chartDataList.length < 1) return;
    setSelectedChartData(chartDataList[0]);
    setSelectedColorData(colorParette[0]);
  }, [colorParette, chartDataList]);

  //api response(금일배송일정) 그룹화
  function groupingData(data: DeliveryPreviewType[]) {
    let groupedDataList: DeliveryPreviewGroupType[] = [];
    data.forEach((deliveryAssignment, index) => {
      const existingGroup = groupedDataList.find(
        (groupedData) =>
          groupedData.deliveryPersonName ===
          deliveryAssignment.deliveryPersonName
      );
      if (existingGroup) {
        groupedDataList = groupedDataList.map((groupData) =>
          groupData.deliveryPersonName === deliveryAssignment.deliveryPersonName
            ? {
                deliveryPersonName: groupData.deliveryPersonName,
                deliveryAssignmentList: [
                  ...groupData.deliveryAssignmentList,
                  deliveryAssignment,
                ],
              }
            : groupData
        );
      } else {
        const newGroup: DeliveryPreviewGroupType = {
          deliveryPersonName: deliveryAssignment.deliveryPersonName,
          deliveryAssignmentList: [deliveryAssignment],
        };
        groupedDataList.push(newGroup);
      }
    });
    //차트데이터 형식으로 변환함수 호출
    transferToChartData(groupedDataList);
  }

  //차트데이터 형식으로 변환
  function transferToChartData(data: DeliveryPreviewGroupType[]) {
    //state set할 최종데이터배열
    let chartDataList: DataGroupType[] = [];
    let parreteDataList: ColorParreteGroupType[] = [];

    //묶음데이터 순회(배송일정 그룹)
    data.forEach((deliveryData) => {
      //배송원별 차트, 컬러배열
      let dataList: DataType[] = [];
      let colorParetteList: string[] = [];

      //배송일정 전부 순회하며
      deliveryData.deliveryAssignmentList.forEach((deliveryAssingment) => {
        //차트컬러 채움(인덱스별. 연속되지않음)
        let color;
        if (deliveryAssingment.deliveryStatus === 'COMPLETED') {
          color = fillColor;
        } else if (deliveryAssingment.deliveryStatus === 'IN_PROGRESS') {
          color = processColor;
        } else {
          color = emptyColor;
        }

        //차트데이터에도 색상정보 대입(우측 dot color용)
        colorParetteList.push(color);
        const data: DataType = {
          id: deliveryAssingment.id.toString(),
          label: deliveryAssingment.custDes,
          value: 1,
          color: color,
        };
        dataList.push(data);
      });
      //배송원 그룹화된 차트
      const chartData: DataGroupType = {
        deliveryPersonName: deliveryData.deliveryPersonName,
        dataList: dataList,
      };
      //배송원 그룹화된 컬러
      const parreteData: ColorParreteGroupType = {
        deliveryPersonName: deliveryData.deliveryPersonName,
        colorList: colorParetteList,
      };
      chartDataList.push(chartData);
      parreteDataList.push(parreteData);
    });
    //최종 state set
    setChartDataList(chartDataList);
    setColorParette(parreteDataList);
  }

  //api호출 및 data init callback
  useEffect(() => {
    fetchDeliveryAssignmentDailyPreviewGetApi(
      getYearMonthDateFromDate(new Date()).dateString
    );
  }, []);

  //배송원 변경하기(화살표)
  function changeDeliveryGroup(type: 1 | -1) {
    const currentIndex = chartDataList.findIndex(
      (data) =>
        data.deliveryPersonName === selectedChartData?.deliveryPersonName
    );
    let newIndex;
    if (type === 1) {
      if (currentIndex < chartDataList.length - 1) {
        newIndex = currentIndex + 1;
      } else {
        newIndex = 0;
      }
    } else {
      if (currentIndex === 0) {
        newIndex = chartDataList.length - 1;
      } else {
        newIndex = currentIndex - 1;
      }
    }
    setSelectedChartData(chartDataList[newIndex]);
    setSelectedColorData(colorParette[newIndex]);
  }

  return (
    //container size fix
    <div
      style={{
        width: '100%',
        height: '268px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {selectedChartData !== null && selectedColorData !== null ? (
        <>
          {/* for spacing */}
          <div style={{ flex: '0 0 25px' }} />

          {/* ResponsivePie 반응형 차트 사이즈 고정용 ChartSizeFixer */}
          <ChartSizeFixer>
            <ResponsivePie
              data={selectedChartData.dataList}
              colors={selectedColorData.colorList}
              margin={{ top: 1, right: 1, bottom: 1, left: -150 }} //차트위치조절
              // 기타 속성
              startAngle={20}
              endAngle={340}
              innerRadius={0.9}
              padAngle={3}
              cornerRadius={6}
              activeOuterRadiusOffset={6}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 0.2]],
              }}
              enableArcLinkLabels={false}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextOffset={8}
              arcLinkLabelsTextColor='#333333'
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: 'color' }}
              enableArcLabels={false}
              arcLabelsRadiusOffset={0.55}
              arcLabelsSkipAngle={10}
              // 커스텀 툴팁(차트호버시)
              tooltip={(e) => <ChartTooltip>{e.datum.data.label}</ChartTooltip>}
              arcLabelsTextColor={{
                from: 'color',
                modifiers: [['darker', 2]],
              }}
              //호버액션 활성화
              isInteractive={true}
              //우측 dot과 거래처명 label용 설정.
              legends={[
                {
                  anchor: 'right',
                  direction: 'column',
                  justify: false,
                  translateX: 30,
                  translateY: 0,
                  itemsSpacing: 4,
                  itemWidth: 180,
                  itemHeight: 22,
                  itemTextColor: '#999',
                  itemDirection: 'left-to-right',
                  itemOpacity: 1,
                  symbolSize: 14,
                  symbolShape: 'circle',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemTextColor: '#000',
                      },
                    },
                  ],
                },
              ]}
            />
          </ChartSizeFixer>
          {/* 배송원 변경 영역 */}
          <StyledH4>
            <div
              style={{
                width: '40px',
                textAlign: 'end',
                height: '30px',
                alignContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => changeDeliveryGroup(-1)}
            >
              <IoIosArrowBack />
            </div>
            <div
              style={{
                paddingBottom: '3px',
                fontSize: '18px',
                color: theme.colors.charcoal,
              }}
            >
              {selectedChartData?.deliveryPersonName}
            </div>
            <div
              style={{
                width: '40px',
                textAlign: 'start',
                height: '30px',
                alignContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => changeDeliveryGroup(1)}
            >
              <IoIosArrowForward />
            </div>
          </StyledH4>
        </>
      ) : (
        <StyledH4>배송일정이 없습니다</StyledH4>
      )}
    </div>
  );
};

const ChartTooltip = styled.div`
  padding: 7px;
  background-color: white;
  border: 1px solid gray;
  border-radius: 8px;
`;

const ChartSizeFixer = styled.div`
  width: 100%;
  height: 180px;
  padding-right: 15px;
`;

const StyledH4 = styled.h4`
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;
